import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Zap, ShieldCheck, FileText, Truck, Sparkles, ArrowRight, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import SEO from "@/components/app/SEO";
import { pushAnalyticsEvent } from "@/lib/analytics";

const CATEGORY_ICONS = {
  assurance: "🛡️", sante: "🩺", telecom: "📶", energie: "⚡",
  banque: "🏦", loisirs: "✨", mobilite: "🚗", autres: "📦",
};

export default function HomePage() {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => !c.parent_slug)));
    api.get("/organizations?limit=8").then((r) => setPopular(r.data.items));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const query = q.trim();
      if (!query) { setSuggestions([]); return; }
      api.get(`/search/suggest?q=${encodeURIComponent(query)}`).then((r) => {
        setSuggestions(r.data.items);
        pushAnalyticsEvent("organization_search", {
          results_count: (r.data.items || []).length,
          has_results: (r.data.items || []).length > 0,
          query_length: query.length,
        }, { once: false });
      });
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const goOrg = (slug, category) => {
    pushAnalyticsEvent("organization_selected", {
      organization_slug: slug,
      organization_category: category,
      selection_source: "hero_search",
    }, { once: false });
    nav(`/organisme/${slug}`);
  };

  return (
    <div>
      <SEO
        title="Résiliez vos contrats en quelques clics"
        description="Recherchez votre organisme, générez votre courrier, envoyez-le en recommandé et récupérez toutes vos preuves depuis un seul espace."
        canonical="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-medium mb-6" data-testid="hero-badge">
              <Sparkles className="h-3 w-3" />
              Nouveau · Envoi en recommandé + coffre-fort de preuves
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Résiliez vos contrats
              <br />
              <span className="text-gradient-brand">sans vous perdre</span> dans les démarches.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
              Recherchez votre organisme, générez votre courrier, envoyez-le en recommandé et suivez toutes vos preuves — depuis un seul espace sécurisé.
            </p>

            <div className="relative max-w-2xl">
              <div className="flex items-center gap-2 rounded-full border border-border bg-card shadow-sm p-2 pl-5">
                <Search className="h-5 w-5 text-muted-foreground shrink-0" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Free, Netflix, MAIF, EDF, Basic-Fit..."
                  className="border-0 shadow-none focus-visible:ring-0 h-11 text-base"
                />
                <Button
                  data-testid="hero-search-btn"
                  className="rounded-full bg-gradient-brand text-white border-0 hover:opacity-90 h-11 px-5"
                  onClick={() => nav(`/annuaire?q=${encodeURIComponent(q)}`)}
                >
                  Rechercher
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              {q && suggestions.length > 0 && (
                <div className="absolute z-30 mt-2 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden" data-testid="hero-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.slug}
                      onClick={() => goOrg(s.slug, s.categories?.[0])}
                      className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors duration-200 flex items-center justify-between border-b border-border last:border-0"
                      data-testid={`suggest-${s.slug}`}
                    >
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground">{s.categories?.[0]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" />Rédaction assistée</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" />Envoi recommandé</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" />Coffre-fort de preuves</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" />Paiement PayPal</span>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Annuaire</p>
            <h2 className="text-3xl font-bold tracking-tight">Catégories populaires</h2>
          </div>
          <Button asChild variant="ghost" data-testid="see-all-categories-btn">
            <Link to="/annuaire">Tout voir <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.slice(0, 8).map((c) => (
            <Link
              key={c.slug}
              to={`/categorie/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="card-elevated p-5 group"
            >
              <div className="text-3xl mb-3">{CATEGORY_ICONS[c.slug] || "📄"}</div>
              <div className="font-semibold text-sm mb-1">{c.name}</div>
              <div className="text-xs text-muted-foreground">Résilier facilement</div>
            </Link>
          ))}
        </div>
      </section>

      {/* POPULAR ORGS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Organismes populaires</p>
          <h2 className="text-3xl font-bold tracking-tight">Les plus recherchés</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {popular.map((o) => (
            <Link
              key={o.slug}
              to={`/organisme/${o.slug}`}
              data-testid={`popular-org-${o.slug}`}
              className="card-elevated p-5"
            >
              <div className="text-sm font-semibold mb-1">{o.name}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{o.description}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
                Résilier <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Comment ça marche</p>
          <h2 className="text-3xl font-bold tracking-tight">Quatre étapes, aucune paperasse.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { i: 1, icon: Search, title: "Cherchez", text: "Trouvez votre organisme parmi des milliers de fiches." },
            { i: 2, icon: FileText, title: "Générez", text: "Répondez à quelques questions, votre courrier s'écrit tout seul." },
            { i: 3, icon: Truck, title: "Envoyez", text: "Nous envoyons votre courrier en recommandé et le suivons pour vous." },
            { i: 4, icon: ShieldCheck, title: "Conservez", text: "Toutes vos preuves archivées dans un coffre-fort numérique." },
          ].map((s) => (
            <div key={s.i} className="card-elevated p-6" data-testid={`how-step-${s.i}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-gradient-brand flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-white" />
                </div>
                <span className="font-mono text-xs text-muted-foreground">0{s.i}</span>
              </div>
              <h3 className="font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Pourquoi Résiliation Express</p>
            <h2 className="text-3xl font-bold tracking-tight mb-6">
              La sérénité, du premier clic
              <br />au dernier justificatif.
            </h2>
            <ul className="space-y-4">
              {[
                { t: "Fiches d'organismes vérifiées", d: "Adresses de résiliation, préavis, motifs, justificatifs : tout est structuré et à jour." },
                { t: "Envoi recommandé intégré", d: "Le courrier est produit, envoyé et suivi. Vous récupérez toutes les preuves." },
                { t: "Coffre-fort numérique", d: "Preuve de dépôt, accusé de réception, courrier signé — archivés de manière sécurisée." },
                { t: "Paiement PayPal", d: "Compte PayPal, carte bancaire, Paiement en 4X (sous réserve d'éligibilité PayPal)." },
              ].map((f) => (
                <li key={f.t} className="flex gap-3">
                  <Check className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{f.t}</div>
                    <div className="text-sm text-muted-foreground">{f.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <Card className="p-8 card-elevated border-border/50">
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-6">
              <Zap className="h-3 w-3 text-yellow-500" /> DÉMONSTRATION
            </div>
            <div className="space-y-4 font-mono text-sm">
              <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Organisme</span><span className="font-semibold">Netflix</span></div>
              <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Motif</span><span className="font-semibold">Fin d'engagement</span></div>
              <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Préavis</span><span className="font-semibold">Immédiat</span></div>
              <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Envoi</span><span className="font-semibold">Recommandé A/R</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Statut</span><span className="text-green-600 font-semibold">✓ Livré</span></div>
            </div>
            <Button asChild className="w-full mt-8 bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="hero-cta-start">
              <Link to="/resilier">Commencer ma résiliation</Link>
            </Button>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-2xl bg-primary p-10 md:p-16 text-primary-foreground overflow-hidden relative">
          <div className="relative z-10 max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Prêt à résilier sereinement ?</h2>
            <p className="text-primary-foreground/80 mb-8 text-lg">
              Créez votre compte gratuitement. Vous ne payez que si vous choisissez l'envoi assisté.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" variant="secondary" data-testid="cta-register-btn">
                <Link to="/inscription">Créer mon compte</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10" data-testid="cta-start-btn">
                <Link to="/resilier">Démarrer maintenant</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
